/**
 * Scribe (lot 2) – prise de son d'un enregistrement : le micro, et en option le son des participants à distance
 * (partage d'écran de Teams avec son audio), plus la mesure du niveau qui sert à la pause sur silence.
 *
 * - Micro seul : le MediaRecorder enregistre directement le flux du micro (comme au lot 1c) ; un AudioContext ne sert
 *   qu'à mesurer le niveau. Micro changé : nouveau flux, donc nouvelle séquence (MoteurScribe.changerFlux).
 * - Avec le son de Teams : Web Audio mélange le micro et la piste audio du partage dans un MediaStreamDestination, que
 *   le MediaRecorder enregistre. La piste vidéo du partage est arrêtée tout de suite (seul le son compte). Partage
 *   terminé : le micro continue seul dans le même flux ; un nouveau partage se rebranche sur le mélange.
 * - Pause sur silence : `DetecteurSilence` reçoit le niveau (valeur efficace) toutes les MESURE_SILENCE_MS ; après
 *   SILENCE_AVANT_PAUSE_MS sous SEUIL_SILENCE, il demande la pause ; au premier son, la reprise. Niveau inconnu
 *   (AudioContext suspendu, mesure impossible) = du son : jamais de pause sur une mesure douteuse.
 * Partage du son de Teams : Edge et Chrome seulement (getDisplayMedia avec audio) ; ailleurs l'option est masquée.
 */

/** Silence continu avant la pause de l'enregistrement. */
export const SILENCE_AVANT_PAUSE_MS = 60_000;
/** Valeur efficace sous laquelle on parle de silence (≈ −42 dBFS) : la parole, même lointaine, est au-dessus. */
export const SEUIL_SILENCE = 0.008;
/** Mesure du niveau pour la pause sur silence (au plus une par seconde quand l'onglet est masqué). */
export const MESURE_SILENCE_MS = 250;

export type DecisionSilence = 'pause' | 'reprise' | null;

/** Décide de la pause (60 s de silence) et de la reprise (premier son) d'après les mesures successives. */
export class DetecteurSilence {
  private dernierSon: number | null = null;
  private pauseVue = false;

  constructor(private readonly delaiMs = SILENCE_AVANT_PAUSE_MS, private readonly seuil = SEUIL_SILENCE) {}

  /** Une mesure : niveau (null = inconnu, compté comme du son), instant, enregistrement en pause ou non. */
  mesure(niveau: number | null, maintenant: number, enPause: boolean): DecisionSilence {
    // Début de la surveillance, ou reprise venue d'ailleurs (« Reprendre maintenant », micro changé) : le compte repart.
    if (this.dernierSon === null || (this.pauseVue && !enPause)) this.dernierSon = maintenant;
    this.pauseVue = enPause;
    if (niveau === null || niveau >= this.seuil) {
      this.dernierSon = maintenant;
      return enPause ? 'reprise' : null;
    }
    return !enPause && maintenant - this.dernierSon >= this.delaiMs ? 'pause' : null;
  }
}

/** Valeur efficace d'un échantillon (−1 à 1). */
export function valeurEfficace(mesures: Float32Array): number {
  let somme = 0;
  for (const v of mesures) somme += v * v;
  return mesures.length ? Math.sqrt(somme / mesures.length) : 0;
}

/** Le navigateur sait partager le son d'un onglet ou de l'écran : Edge et Chrome (moteur Chromium). */
export function partageSonPossible(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getDisplayMedia !== 'function') return false;
  const marques = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData?.brands;
  if (marques?.length) return marques.some((m) => /Chromium|Google Chrome|Microsoft Edge/.test(m.brand));
  return /\b(Chrome|Edg)\//.test(navigator.userAgent) && !/\bFirefox\//.test(navigator.userAgent);
}

/** Refus d'un partage : `refuse` (fenêtre fermée, autorisation refusée), `sansSon` (« Partager l'audio » non coché). */
export class ErreurPartage extends Error {
  constructor(public readonly motif: 'refuse' | 'sansSon') { super(motif); }
}

/**
 * Demande à l'utilisateur l'onglet de Teams (Teams dans le navigateur) ou l'écran entier (application Teams) avec son
 * son ; la vidéo est arrêtée aussitôt. À appeler dans le geste de l'utilisateur (clic).
 */
export async function partagerSon(): Promise<MediaStream> {
  let flux: MediaStream;
  try {
    flux = await navigator.mediaDevices.getDisplayMedia({
      video: true, audio: true,
      // Options de Chromium : son du système proposé pour l'écran entier, onglet d'AGORA exclu.
      systemAudio: 'include', selfBrowserSurface: 'exclude',
    } as DisplayMediaStreamOptions);
  } catch {
    throw new ErreurPartage('refuse');
  }
  flux.getVideoTracks().forEach((p) => p.stop());
  if (!flux.getAudioTracks().some((p) => p.readyState === 'live')) {
    flux.getTracks().forEach((p) => p.stop());
    throw new ErreurPartage('sansSon');
  }
  return flux;
}

const pisteAudio = (f: MediaStream | null) => f?.getAudioTracks().find((p) => p.readyState === 'live') ?? null;

/**
 * Prise de son d'un enregistrement : micro (+ son de Teams), flux à enregistrer et niveau mesuré. Créée au démarrage
 * (ou à la reprise) dans le geste de l'utilisateur, fermée à la fin de l'enregistrement.
 */
export class PriseDeSon {
  private ctx: AudioContext | null = null;
  private analyse: AnalyserNode | null = null;
  private melange: MediaStreamAudioDestinationNode | null = null;
  private readonly sources = new Map<MediaStream, MediaStreamAudioSourceNode>();
  private mesures: Float32Array<ArrayBuffer> | null = null;

  constructor(public micro: MediaStream | null, public ecran: MediaStream | null = null) {
    const Contexte = typeof window !== 'undefined' ? window.AudioContext : undefined;
    if (Contexte) {
      try {
        this.ctx = new Contexte();
        void this.ctx.resume().catch(() => undefined);
        this.analyse = this.ctx.createAnalyser();
        this.analyse.fftSize = 2048;
        this.mesures = new Float32Array(this.analyse.fftSize);
      } catch {
        this.ctx = null;
      }
    }
    if (ecran) this.creerMelange();
    this.brancher(micro);
    this.brancher(ecran);
  }

  /** Le flux enregistré mêle le micro et le son de Teams. */
  get mixe(): boolean { return !!this.melange; }

  /** Flux à enregistrer : le mélange (micro + son de Teams), sinon le micro. */
  get sortie(): MediaStream | null { return this.melange?.stream ?? this.micro; }

  /** Le son de Teams est partagé et arrive encore. */
  get ecranActif(): boolean { return !!pisteAudio(this.ecran); }

  /** Niveau (valeur efficace) du son enregistré, ou null s'il ne peut pas être mesuré. */
  niveau(): number | null {
    if (!this.ctx || this.ctx.state !== 'running' || !this.analyse || !this.mesures || !this.sources.size) return null;
    this.analyse.getFloatTimeDomainData(this.mesures);
    return valeurEfficace(this.mesures);
  }

  /** Nouveau micro (choisi, ou remplaçant d'un micro perdu) ; null : plus de micro (le son de Teams continue seul). */
  changerMicro(nouveau: MediaStream | null): void {
    this.debrancher(this.micro);
    this.micro = nouveau;
    this.brancher(nouveau);
  }

  /**
   * Son de Teams partagé (au démarrage ou pendant l'enregistrement) : branché sur le mélange, créé au besoin (vrai :
   * le flux à enregistrer a changé, nouvelle séquence). Faux si le mélange est impossible (Web Audio absent).
   */
  ajouterEcran(ecran: MediaStream): { possible: boolean; nouveauFlux: boolean } {
    if (!this.ctx) return { possible: false, nouveauFlux: false };
    const nouveauFlux = !this.melange;
    if (nouveauFlux) {
      this.creerMelange();
      const micro = this.micro ? this.sources.get(this.micro) : undefined;
      if (micro && this.melange) micro.connect(this.melange);
    }
    if (this.ecran) { this.debrancher(this.ecran); this.ecran.getTracks().forEach((p) => p.stop()); }
    this.ecran = ecran;
    this.brancher(ecran);
    return { possible: true, nouveauFlux };
  }

  /** Arrête le partage du son de Teams (le micro continue seul dans le même flux). */
  retirerEcran(): void {
    this.debrancher(this.ecran);
    this.ecran?.getTracks().forEach((p) => p.stop());
    this.ecran = null;
  }

  /** Fin de l'enregistrement : mélange et mesure fermés ; `sources` : micro et partage rendus aussi. */
  fermer(sources = true): void {
    for (const s of this.sources.values()) { try { s.disconnect(); } catch { /* déjà débranché */ } }
    this.sources.clear();
    if (sources) {
      this.micro?.getTracks().forEach((p) => p.stop());
      this.ecran?.getTracks().forEach((p) => p.stop());
    }
    this.melange?.stream.getTracks().forEach((p) => p.stop());
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }

  private creerMelange() {
    if (!this.ctx || this.melange) return;
    try { this.melange = this.ctx.createMediaStreamDestination(); } catch { this.melange = null; }
  }

  private brancher(flux: MediaStream | null) {
    if (!this.ctx || !flux || !pisteAudio(flux) || this.sources.has(flux)) return;
    try {
      const source = this.ctx.createMediaStreamSource(flux);
      if (this.analyse) source.connect(this.analyse);
      if (this.melange) source.connect(this.melange);
      this.sources.set(flux, source);
    } catch { /* piste terminée entre-temps */ }
  }

  private debrancher(flux: MediaStream | null) {
    const source = flux ? this.sources.get(flux) : undefined;
    if (!source || !flux) return;
    try { source.disconnect(); } catch { /* déjà débranché */ }
    this.sources.delete(flux);
  }
}
